import { initializeApp, FirebaseApp } from "firebase/app";
import { getFunctions, Functions, connectFunctionsEmulator } from "firebase/functions";
import { getAnalytics, Analytics, isSupported as isAnalyticsSupported, setAnalyticsCollectionEnabled } from "firebase/analytics";
import { useConsentStore } from '@/stores/consentStore';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken, AppCheck } from "firebase/app-check";

let app: FirebaseApp | null = null;
let functions: Functions | null = null;
let analytics: Analytics | null = null;
useConsentStore.subscribe(state => {
  if (analytics) setAnalyticsCollectionEnabled(analytics, state.consent.analytics === 'granted');
});
let appCheck: AppCheck | null = null;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, 
};

async function initializeFirebase() {
  if (app) return; 

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.error("Firebase API Key or Project ID is missing for marketing site.");
    return;
  }

  try {
    app = initializeApp(firebaseConfig);
    functions = getFunctions(app, import.meta.env.VITE_FIREBASE_REGION || 'us-central1');

    const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (recaptchaKey) {
        if (import.meta.env.MODE === 'development' || location.hostname === "localhost") {
            (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
        appCheck = initializeAppCheck(app, {
            provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
            isTokenAutoRefreshEnabled: true
        });
    }

    if (import.meta.env.MODE === 'development' || import.meta.env.VITE_EMULATOR_FUNCTIONS_HOST) {
        const functionsHost = import.meta.env.VITE_EMULATOR_FUNCTIONS_HOST || 'localhost';
        const functionsPort = parseInt(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT || '5000', 10);
        try {
            connectFunctionsEmulator(functions, functionsHost, functionsPort);
        } catch (e) {
            console.error("[Marketing] Failed to connect to Functions emulator:", e);
        }
    }

  } catch (error) {
    console.error("Error initializing Firebase:", error);
    app = null;
    functions = null;
    analytics = null;
  }
}

export async function getFirebaseServices() {
  if (!app) {
    await initializeFirebase();
  }
  try {
    if (app && !analytics && import.meta.env.PROD && location.hostname === 'runofftrack.com' &&
        sessionStorage.getItem('runofftrack.analytics_disabled') !== '1' &&
        useConsentStore.getState().consent.analytics === 'granted' &&
        firebaseConfig.measurementId && await isAnalyticsSupported() &&
        useConsentStore.getState().consent.analytics === 'granted') analytics = getAnalytics(app);
  } catch { /* Analytics must not prevent API access. */ }
  return { app, functions, analytics };
}

/**
 * Custom API Caller for Marketing Site
 */
export async function callApi<RequestData = any, ResponseData = any>(
  endpoint: string,
  data?: RequestData
): Promise<{ data: ResponseData }> {
  await getFirebaseServices(); 

  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (appCheck) {
    try {
        const tokenResult = await getToken(appCheck, false);
        headers['X-Firebase-AppCheck'] = tokenResult.token;
    } catch (error) {
      console.warn('[Firebase] App Check token fetch failed.', error);
    }
  }

  // Ensure data is null if undefined
  const payload = { data: data === undefined ? null : data };

  const response = await fetch(`/api/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  const json = await response.json();

  if (!response.ok) {
    const errorMsg = json.error?.message || json.error || 'Unknown server error';
    throw new Error(errorMsg);
  }

  return { data: json.result as ResponseData };
}

export async function getCallable(name: string) {
    return async (data: any) => {
        return callApi(name, data);
    };
}
